import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  CATEGORY_GROUPS,
  CLEAN_VEHICLE_RULES,
  CLEAN_VERDICTS,
  COUNTRY_TARGETS,
  ENERGY_SOURCES,
  REFERENCE_PERIODS,
  TARGETS_REVIEWED,
  energySourceDefinition,
  explainRule,
  ruleFor,
  targetsFor,
  type CleanVehicleRule,
} from '@dashboard/contracts';
import { useFleetSettings } from '../../api/queries';
import { useSession } from '../../auth/session-context';
import { formatDate, formatNumber, todayIsoDate } from '../../i18n';
import { CleanVerdictBadge } from '../ui/status-badge';
import { ruleSentence } from './fleet-labels';

/**
 * The rules themselves, rendered as what they are: a catalog.
 *
 * ## Why a screen at all
 *
 * Everywhere else in this module the rules are visible only through their
 * effects. A row says "cannot be judged -- no CO2 figure recorded", which tells
 * a reader what went wrong with *that vehicle* and nothing about what the rule
 * wanted. So the questions the module could not answer were the three a person
 * actually arrives with: what does it take to count, why did this one not, and
 * who decided. The first two were answerable only by reading
 * `fleet-compliance.ts`, and the third was answerable nowhere.
 *
 * That is a poor position for a product whose output goes to a regulator. A
 * figure nobody can interrogate is a figure nobody will defend in a meeting --
 * the same argument section 7.4 of the design makes for drilling into the
 * report, applied one level further down, to the rule rather than the rows.
 *
 * ## Read-only, and it says so
 *
 * The last card exists to answer "can I change these?" with a plain no and the
 * reason. Leaving it out would be worse than saying nothing: an administrator
 * who finds a threshold they disagree with and no statement about it goes
 * looking through settings for the control, and concludes the product is
 * missing one rather than that the answer is deliberate.
 *
 * ## Nothing here restates a rule
 *
 * Every number, date, category and fuel flag is read from the catalogs in
 * `@dashboard/contracts` -- the same objects `evaluate` compares against. The
 * sentences come from `explainRule`, which lives beside the tests it describes
 * for exactly this reason. A screen that hard-coded "50 g/km" would keep saying
 * it the day the catalog said 45, and would be this product stating the law
 * incorrectly to the person filing against it.
 */
export function RulesReference() {
  const { t } = useTranslation();
  const [onDate, setOnDate] = useState(() => todayIsoDate());

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{t('fleet.rules.heading')}</h2>
          <div className="card-actions">
            {/* The same control the register carries, doing the same job: the
                2026 cliff is a thing to see rather than be told about, and here
                it moves which rule is marked as the one in force. */}
            <label className="fleet-control" htmlFor="fleet-rules-as-of">
              <span>{t('fleet.rules.asOf')}</span>
              <input
                id="fleet-rules-as-of"
                className="input"
                type="date"
                value={onDate}
                onChange={(event) => setOnDate(event.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="card-body">
          <p className="cell-secondary">{t('fleet.rules.intro')}</p>

          <ol className="rule-list">
            {CLEAN_VEHICLE_RULES.map((rule) => (
              <RuleCard key={rule.id} rule={rule} onDate={onDate} />
            ))}
          </ol>

          <VerdictLegend />
        </div>
      </div>

      <FuelTable />
      <TargetTable />
      <ProvenanceNote />
    </>
  );
}

/* ----------------------------------------------------------------- a rule */

function RuleCard({ rule, onDate }: { rule: CleanVehicleRule; onDate: string }) {
  const { t, i18n } = useTranslation();
  const explanation = explainRule(rule);

  /*
   * Asked of the real selector rather than compared here.
   *
   * `onDate >= rule.from && onDate <= rule.until` would be three characters
   * shorter and would be a second implementation of the one thing this screen
   * must not get independently wrong: which rule applies. Putting the question
   * to `ruleFor` means the badge says "in force" exactly when the evaluator
   * would pick this rule -- including on the two days a year an off-by-one in
   * either bound would show.
   */
  const inForce = ruleFor(rule.appliesTo[0]!, onDate)?.id === rule.id;

  return (
    <li className={`rule-card${inForce ? ' rule-card--in-force' : ''}`}>
      <div className="rule-card__head">
        <h3 className="rule-card__title">{t(rule.labelKey)}</h3>
        <span className={`status-badge status-badge--${inForce ? 'active' : 'neutral'}`}>
          <span className="status-badge__glyph" aria-hidden="true">
            {inForce ? '●' : '○'}
          </span>
          {inForce ? t('fleet.rules.inForce') : t('fleet.rules.notInForce')}
        </span>
      </div>

      <dl className="rule-card__facts">
        <div>
          <dt>{t('fleet.rules.appliesTo')}</dt>
          {/* The code and the word for it. "M2" alone is a category only
              somebody who works with the directive can read, and the people who
              most need this screen are the ones who cannot yet. */}
          <dd>
            {rule.appliesTo
              .map((category) => `${category} — ${t(`fleet.categoryName.${category}`)}`)
              .join(' · ')}
          </dd>
        </div>
        <div>
          <dt>{t('fleet.rules.inForceBetween')}</dt>
          <dd>
            {formatDate(rule.from, i18n.language)} – {formatDate(rule.until, i18n.language)}
          </dd>
        </div>
      </dl>

      <div className="rule-card__test">
        <h4>{t('fleet.rules.cleanWhen')}</h4>
        {/* Said in words rather than drawn as a bulleted "or", because a list
            of alternatives and a list of conditions look identical and mean
            opposite things -- and reading this one as an "and" would have
            somebody hunting for an RDE figure for a battery van. */}
        {explanation.cleanWhen.length > 1 ? (
          <p className="rule-card__hint">{t('fleet.rules.anyOf')}</p>
        ) : null}
        <ul>
          {explanation.cleanWhen.map((requirement) => (
            <li key={requirement.labelKey}>{ruleSentence(t, requirement)}</li>
          ))}
        </ul>
      </div>

      <div className="rule-card__test">
        <h4>{t('fleet.rules.undeterminedWhen')}</h4>
        <ul>
          {explanation.undeterminedWhen.map((requirement) => (
            <li key={requirement.labelKey}>{ruleSentence(t, requirement)}</li>
          ))}
        </ul>
      </div>
    </li>
  );
}

/**
 * What the three badges mean.
 *
 * Rendered with `CleanVerdictBadge` itself rather than described in prose, so
 * the legend cannot come to disagree with the table it explains -- and so the
 * glyph a reader is matching against is literally the glyph they saw.
 */
function VerdictLegend() {
  const { t } = useTranslation();

  return (
    <section className="rule-verdicts" aria-labelledby="fleet-rules-verdicts">
      <h3 id="fleet-rules-verdicts">{t('fleet.rules.verdicts.heading')}</h3>
      <dl className="rule-verdicts__list">
        {CLEAN_VERDICTS.map((verdict) => (
          <div key={verdict}>
            <dt>
              <CleanVerdictBadge verdict={verdict} />
            </dt>
            <dd>{t(`fleet.rules.verdicts.${verdict}`)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/* ----------------------------------------------------------------- fuels */

/**
 * The fuel catalog, which is half of the heavy-duty rule.
 *
 * Three flags per fuel, and the third is the one worth a table of its own: a
 * biofuel is an alternative fuel whose qualification depends on a feedstock
 * this product does not record, so it produces "cannot be judged" rather than a
 * pass. That is surprising enough that somebody meeting it on a row deserves
 * somewhere to see it stated as a property of the fuel.
 */
function FuelTable() {
  const { t } = useTranslation();

  const fuels = ENERGY_SOURCES.map(energySourceDefinition).sort((a, b) => a.order - b.order);
  const yesNo = (value: boolean) =>
    value ? t('fleet.rules.fuels.yes') : t('fleet.rules.fuels.no');

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">{t('fleet.rules.fuels.heading')}</h2>
      </div>
      <div className="card-body">
        <p className="cell-secondary">{t('fleet.rules.fuels.intro')}</p>
      </div>
      <div className="card-body card-body--flush">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('fleet.rules.fuels.fuel')}</th>
                <th scope="col">{t('fleet.rules.fuels.zeroEmission')}</th>
                <th scope="col">{t('fleet.rules.fuels.alternative')}</th>
                <th scope="col">{t('fleet.rules.fuels.feedstock')}</th>
              </tr>
            </thead>
            <tbody>
              {fuels.map((fuel) => (
                <tr key={fuel.key}>
                  <th scope="row">{t(fuel.labelKey)}</th>
                  {/* Words rather than ticks. A tick and an empty cell are the
                      same cell to anybody printing this in monochrome, and
                      section 18 forbids carrying meaning in appearance alone. */}
                  <td>{yesNo(fuel.zeroEmission)}</td>
                  <td>{yesNo(fuel.alternative)}</td>
                  <td>{yesNo(fuel.ilucSensitive)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-footer">
        <span className="cell-secondary">{t('fleet.rules.fuels.feedstockHint')}</span>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- targets */

/**
 * The minimum shares, for the country the deployment is configured for.
 *
 * One country rather than the whole catalog: the others are not this
 * authority's obligation, and a table offering a choice would invite somebody
 * to read their figure off the wrong row. Which country applies is a setting,
 * and the card names it.
 */
function TargetTable() {
  const { t, i18n } = useTranslation();
  const settings = useFleetSettings();
  const countryCode = settings.data?.countryCode ?? null;
  const known = countryCode !== null && COUNTRY_TARGETS[countryCode] !== undefined;

  const asPercent = (value: number) =>
    `${formatNumber(value, i18n.language, { maximumFractionDigits: 1 })}%`;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">
          {t('fleet.rules.targets.heading', { country: countryCode ?? '—' })}
        </h2>
      </div>

      <div className="card-body">
        {/* The same sentence the report carries, from the same constant. While
            the figures have not been read as law, a table of them presented
            without the caveat is the more dangerous half of the report. */}
        {!TARGETS_REVIEWED ? (
          <p role="note" className="fleet-note fleet-note--warning">
            {t('fleet.provisional.targetsUnreviewed')}
          </p>
        ) : null}
        <p className="cell-secondary">{t('fleet.rules.targets.intro')}</p>
      </div>

      {known ? (
        <div className="card-body card-body--flush">
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('fleet.rules.targets.group')}</th>
                  {REFERENCE_PERIODS.map((period) => (
                    <th key={period.id} scope="col" className="numeric">
                      {t(period.labelKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CATEGORY_GROUPS.map((group) => (
                  <tr key={group}>
                    <th scope="row">{t(`fleet.categoryGroup.${group}`)}</th>
                    {REFERENCE_PERIODS.map((period) => {
                      const target = targetsFor(countryCode, period.id, group);
                      return (
                        <td key={period.id} className="numeric">
                          {target ? asPercent(target.minimumCleanPercent) : '—'}
                          {/* The bus sub-target, on its own line rather than in
                              its own column: it exists for one of the three
                              rows, and a column empty for two thirds of a table
                              reads as missing data. */}
                          {target?.minimumZeroEmissionPercent != null ? (
                            <div className="cell-secondary">
                              {t('fleet.rules.targets.ofWhichZeroEmission', {
                                percent: formatNumber(
                                  target.minimumZeroEmissionPercent,
                                  i18n.language,
                                  { maximumFractionDigits: 1 },
                                ),
                              })}
                            </div>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card-body">
          <p className="cell-secondary">{t('fleet.rules.targets.unknownCountry')}</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ provenance */

/** Where the rules come from, and what an administrator may change instead. */
function ProvenanceNote() {
  const { t } = useTranslation();
  const { canAny } = useSession();

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">{t('fleet.rules.source.heading')}</h2>
      </div>
      <div className="card-body">
        <p className="cell-secondary">{t('fleet.rules.source.body')}</p>
        {/* Offered only to somebody who can act on it. A link to a settings
            page that refuses them is a worse answer than the paragraph alone. */}
        {canAny('fleet.agreement.manage') ? (
          <p className="cell-secondary">
            {t('fleet.rules.source.configurable')}{' '}
            <Link to="/settings/fleet">{t('settings.sections.fleet.title')}</Link>
          </p>
        ) : null}
      </div>
    </div>
  );
}
